import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { OrgContext } from '../context/OrgContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  getMembershipPayInfo,
  regenerateMembershipPayLink,
} from '../services/membershipService';
import './MembershipPay.css';

/**
 * Guest-facing membership payment landing.
 *
 * Route: /o/:orgSlug/membership/pay/:purchaseId
 *
 * Instead of emailing the raw Paymob iframe URL to guests (opaque, no
 * branding, no "what am I paying for" context), the approval email now
 * links here. Guest sees their name, membership type, price, expiry,
 * then clicks Pay → we hand off to Paymob.
 *
 * States (based on backend `reason`):
 *   valid           → show details + Pay Now
 *   already_paid    → info panel, redirect to org page
 *   link_expired    → offer Generate New Link (regenerates via API)
 *   no_link         → same UX as expired (no token stored yet)
 *   not_found       → 404 panel
 *   membership_missing → 404 panel
 */
const MembershipPay = () => {
  const { purchaseId, orgSlug: routeOrgSlug } = useParams();
  const navigate = useNavigate();
  const orgCtx = useContext(OrgContext);
  const orgSlug = orgCtx?.orgSlug || routeOrgSlug || null;

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null); // full response { valid, reason?, data? }
  const [error, setError] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());

  const fetchInfo = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getMembershipPayInfo(purchaseId);
      setInfo(res);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        err?.message ||
        'Could not load payment details.'
      );
    } finally {
      setLoading(false);
    }
  }, [purchaseId]);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  // Update the expiry countdown every second while the page is open.
  useEffect(() => {
    if (!info?.valid) return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [info?.valid]);

  const handlePay = () => {
    const url = info?.data?.paymentUrl;
    if (!url) return;
    // Hard navigation (not history.push) — we're leaving the SPA for
    // Paymob's iframe host.
    window.location.href = url;
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const res = await regenerateMembershipPayLink(purchaseId);
      if (res?.success) {
        // Re-fetch info to show fresh expiry + Pay button.
        await fetchInfo();
      } else {
        setError(res?.message || 'Could not generate a new link.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not generate a new link.');
    } finally {
      setRegenerating(false);
    }
  };

  const goToMembership = () => {
    navigate(orgSlug ? `/o/${orgSlug}/membership` : '/');
  };

  // Format a human-readable countdown ("expires in 12h 34m 05s")
  const countdown = (() => {
    const iso = info?.data?.expiresAt;
    if (!iso) return null;
    const diff = new Date(iso).getTime() - nowTick;
    if (diff <= 0) return { expired: true, label: 'Expired' };
    const h = Math.floor(diff / 3_600_000);
    const m = Math.floor((diff % 3_600_000) / 60_000);
    const s = Math.floor((diff % 60_000) / 1000);
    const label = h > 0
      ? `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`
      : `${m}m ${String(s).padStart(2, '0')}s`;
    return { expired: false, label };
  })();

  return (
    <>
      <Navbar />
      <div className="mpay-page">
        <div className="mpay-orb mpay-orb-1" />
        <div className="mpay-orb mpay-orb-2" />
        <div className="mpay-orb mpay-orb-3" />

        <div className="mpay-card">
          {loading && (
            <div className="mpay-state">
              <div className="mpay-spinner" />
              <p className="mpay-state-text">Loading payment details…</p>
            </div>
          )}

          {!loading && error && (
            <div className="mpay-state mpay-state-error">
              <div className="mpay-icon mpay-icon-error">!</div>
              <h2 className="mpay-title">Something went wrong</h2>
              <p className="mpay-subtitle">{error}</p>
              <button className="mpay-btn mpay-btn-secondary" onClick={fetchInfo}>
                Try again
              </button>
            </div>
          )}

          {!loading && !error && info && !info.valid && info.reason === 'already_paid' && (
            <div className="mpay-state mpay-state-info">
              <div className="mpay-icon mpay-icon-success">✓</div>
              <h2 className="mpay-title">Already paid</h2>
              <p className="mpay-subtitle">
                This membership has been paid successfully.
                Your welcome email includes your membership code.
              </p>
              <button className="mpay-btn mpay-btn-primary" onClick={goToMembership}>
                Go to my membership
              </button>
            </div>
          )}

          {!loading && !error && info && !info.valid &&
            (info.reason === 'link_expired' || info.reason === 'no_link') && (
            <div className="mpay-state mpay-state-warning">
              <div className="mpay-icon mpay-icon-warning">⏱</div>
              <h2 className="mpay-title">
                {info.reason === 'link_expired' ? 'Payment link expired' : 'No active payment link'}
              </h2>
              <p className="mpay-subtitle">
                {info.reason === 'link_expired'
                  ? 'This payment link has expired. Generate a new one below to complete your membership payment.'
                  : 'A payment link hasn\'t been generated yet. Click below to create one now.'}
              </p>
              <button
                className="mpay-btn mpay-btn-primary"
                onClick={handleRegenerate}
                disabled={regenerating}
              >
                {regenerating ? 'Generating…' : 'Generate new link'}
              </button>
            </div>
          )}

          {!loading && !error && info && !info.valid &&
            (info.reason === 'not_found' || info.reason === 'membership_missing') && (
            <div className="mpay-state mpay-state-error">
              <div className="mpay-icon mpay-icon-error">?</div>
              <h2 className="mpay-title">Payment not found</h2>
              <p className="mpay-subtitle">
                We couldn't find this payment. The link may be incorrect,
                or the membership may have been removed. Please contact support.
              </p>
              <button className="mpay-btn mpay-btn-secondary" onClick={goToMembership}>
                Back to memberships
              </button>
            </div>
          )}

          {!loading && !error && info?.valid && info.data && (
            <div className="mpay-body">
              <div className="mpay-header">
                <div className="mpay-badge">Membership Payment</div>
                <h1 className="mpay-org-name">
                  {info.data.org.name || 'Complete Your Membership'}
                </h1>
              </div>

              <div className="mpay-summary">
                <div className="mpay-summary-row">
                  <span className="mpay-label">Membership type</span>
                  <span className="mpay-value">{info.data.membershipTypeName}</span>
                </div>
                <div className="mpay-summary-row">
                  <span className="mpay-label">Name</span>
                  <span className="mpay-value">
                    {info.data.guestName}
                    {info.data.coupleName ? ` & ${info.data.coupleName}` : ''}
                  </span>
                </div>
                <div className="mpay-summary-row">
                  <span className="mpay-label">Email</span>
                  <span className="mpay-value mpay-value-mono">{info.data.email}</span>
                </div>
              </div>

              <div className="mpay-price-block">
                <span className="mpay-price-label">Amount due</span>
                <div className="mpay-price">
                  <span className="mpay-price-amount">
                    {Number(info.data.price).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                  <span className="mpay-price-currency">{info.data.currency}</span>
                </div>
              </div>

              {countdown && !countdown.expired && (
                <div className="mpay-countdown">
                  <span className="mpay-countdown-dot" />
                  Link expires in <strong>{countdown.label}</strong>
                </div>
              )}

              <button
                className="mpay-btn mpay-btn-primary mpay-btn-pay"
                onClick={handlePay}
                disabled={!info.data.paymentUrl}
              >
                Pay {Number(info.data.price).toLocaleString()} {info.data.currency}
                <span className="mpay-btn-arrow">→</span>
              </button>

              <p className="mpay-secure">
                <span className="mpay-lock">🔒</span>
                Secured by Paymob. Your card details never touch our servers.
              </p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default MembershipPay;
